import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';

// Public pages
import Home from './pages/public/Home';
import ArticlePage from './pages/public/ArticlePage';
import CategoryPage from './pages/public/CategoryPage';
import EPaperPage from './pages/public/EPaperPage';
import SearchPage from './pages/public/SearchPage';
import AboutPage from './pages/public/AboutPage';

// Admin pages
import AdminLogin from './pages/admin/AdminLogin';
import AdminLayout from './pages/admin/AdminLayout';
import Dashboard from './pages/admin/Dashboard';
import AddEditArticle from './pages/admin/AddEditArticle';
import ManageArticles from './pages/admin/ManageArticles';
import ManageEPapers from './pages/admin/ManageEPapers';
import ManageAds from './pages/admin/ManageAds';
import Analytics from './pages/admin/Analytics';
import ManageCategories from './pages/admin/ManageCategories';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-spinner">Loading...</div>;
  return user ? children : <Navigate to="/admin-secure-login" replace />;
};

/* Global animated background rendered once at root */
function GlobalBackground() {
  return (
    <>
      <div className="global-bg-canvas" aria-hidden="true" />
      <div className="bg-grid-mesh" aria-hidden="true" />
      <div className="bg-scanlines" aria-hidden="true" />
      <div className="orb orb-1" aria-hidden="true" />
      <div className="orb orb-2" aria-hidden="true" />
      <div className="orb orb-3" aria-hidden="true" />
      <div className="orb orb-4" aria-hidden="true" />
      <div className="orb orb-5" aria-hidden="true" />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <LanguageProvider>
          <GlobalBackground />
          <BrowserRouter>
            <Routes>
              {/* Public */}
              <Route path="/" element={<Home />} />
              <Route path="/article/:id" element={<ArticlePage />} />
              <Route path="/category/:category" element={<CategoryPage />} />
              <Route path="/epaper" element={<EPaperPage />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/about" element={<AboutPage />} />

              {/* Hidden Admin Login */}
              <Route path="/admin-secure-login" element={<AdminLogin />} />

              {/* Protected Admin */}
              <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="add-article" element={<AddEditArticle />} />
                <Route path="edit-article/:id" element={<AddEditArticle />} />
                <Route path="manage-articles" element={<ManageArticles />} />
                <Route path="epapers" element={<ManageEPapers />} />
                <Route path="advertisements" element={<ManageAds />} />
                <Route path="analytics" element={<Analytics />} />
                <Route path="categories" element={<ManageCategories />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </LanguageProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;